import React from "react";
import styled, { css } from "styled-components";
import FontText from "../FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "../FontText/FontTextEnums";
import Icon from "../Icon/Icon";
import { ThemeColorEnum, ThemeIconEnum, ThemeIconSizeEnum } from "../Themes/ThemeEnums";
import type { ITheme } from "../Themes/ThemeTypes";
import { SectionCardVariantEnum } from "./SectionCardVariantEnum";

const Card = styled.section<{
  theme: ITheme;
  $accentStripe: boolean;
  $variant: SectionCardVariantEnum;
  $flush: boolean;
  $interactive: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: rgb(
    ${(props) =>
      props.$variant === SectionCardVariantEnum.SURFACE
        ? props.theme.colors.surface
        : props.theme.colors.surface_container}
  );
  color: rgb(${(props) => props.theme.colors.on_surface});
  border-radius: 16px;
  padding: ${(props) => (props.$flush ? "0" : "24px")};
  ${(props) =>
    (props.$interactive || props.$flush) &&
    css`
      overflow: hidden;
    `}
  ${(props) => props.$interactive && `position: relative;`}
  ${(props) => props.$accentStripe && `border-left: 4px solid rgb(${props.theme.colors.primary});`}

  ${(props) =>
    props.$interactive &&
    css`
      cursor: pointer;

      & > * {
        position: relative;
        z-index: 1;
      }

      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background-color: rgb(${props.theme.colors.on_surface});
        opacity: 0;
        pointer-events: none;
        transition: opacity ${props.theme.base.motion.duration.short4}ms
          ${props.theme.base.motion.easing.standard};
      }

      &:hover::after {
        opacity: ${props.theme.base.opacity.hover_state_layer};
      }

      &:active::after {
        opacity: ${props.theme.base.opacity.pressed_state_layer};
      }

      &:focus-visible {
        outline: 2px solid rgb(${props.theme.colors.primary});
        outline-offset: 2px;
      }
    `}

  @media (max-width: ${(props) => props.theme.base.responsive.mobile}px) {
    padding: ${(props) => (props.$flush ? "0" : "16px")};
    border-radius: 12px;
  }
`;

// The header lines up with the list rows below it, which carry 16px of their own.
const Header = styled.div<{ $flush: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  ${(props) => props.$flush && `padding: 16px 16px 0;`}
`;

const HeaderActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface SectionCardProps {
  title?: string;
  icon?: ThemeIconEnum;
  iconNode?: React.ReactNode;
  accentStripe?: boolean;
  variant?: SectionCardVariantEnum;
  flush?: boolean;
  actions?: React.ReactNode;
  onClick?: () => void;
  children?: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  iconNode,
  accentStripe,
  variant,
  flush,
  actions,
  onClick,
  children,
}) => (
  <Card
    $accentStripe={accentStripe ?? false}
    $variant={variant ?? SectionCardVariantEnum.FILLED}
    $flush={flush ?? false}
    $interactive={onClick !== undefined}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    {(title || icon !== undefined || iconNode || actions) && (
      <Header $flush={flush ?? false}>
        {iconNode ??
          (icon !== undefined && (
            <Icon icon={icon} color={ThemeColorEnum.ON_SURFACE} size={ThemeIconSizeEnum.SMALL} />
          ))}
        {title && (
          <FontText
            text={title}
            $color={ThemeColorEnum.ON_SURFACE}
            $fontType={FontTypeEnum.TITLE}
            $fontSize={FontSizeEnum.LARGE}
          />
        )}
        {actions && <HeaderActions>{actions}</HeaderActions>}
      </Header>
    )}
    {children}
  </Card>
);

export default SectionCard;
